import React, { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { motion } from "framer-motion";
import { addressCoordinates, searchCep } from "../../../../extras";
import { validateCEP, validateString } from "../../../../common/validators";
import { ERROR_MESSAGE_ADDRESS_NOT_FOUND } from "../../../../common/helpersTxt";

// SERVICES
import DeliveryRadiusService from "../../../../api/deliveryRadius";
import AddressService from "../../../../api/address";

// MATERIAL UI COMPONENTS
import {
  Alert,
  alpha,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  Paper,
  Stack,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";

// ICONS
import CloseIcon from "@mui/icons-material/Close";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import HomeIcon from "@mui/icons-material/Home";
import MapIcon from "@mui/icons-material/Map";
import NumbersIcon from "@mui/icons-material/Numbers";
import ApartmentIcon from "@mui/icons-material/Apartment";
import BusinessIcon from "@mui/icons-material/Business";
import PublicIcon from "@mui/icons-material/Public";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import CalculateIcon from "@mui/icons-material/Calculate";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import SearchIcon from "@mui/icons-material/Search";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SaveIcon from "@mui/icons-material/Save";
import DirectionsIcon from "@mui/icons-material/Directions";

// COMPONENTS
import MoneyTextField from "../../../Inputs/MoneyTextField";
// Make sure to import this icon
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { formatFinance } from "../../../../utils/formatters";
import { useSelector } from "react-redux";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 24,
    },
  },
};

const buttonVariants = {
  hover: {
    scale: 1.05,
    boxShadow: "0 5px 15px rgba(0,0,0,0.1)",
  },
  tap: {
    scale: 0.95,
  },
};

const resultCardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.3,
      duration: 0.5,
      type: "spring",
      stiffness: 300,
    },
  },
};

export default function AddressForm({ address, setAddress }) {
  const theme = useTheme();

  const deliveryCenterLat = useSelector(
    (state) => state.deliveryCenter.deliveryCenter?.lat
  );
  const deliveryCenterLng = useSelector(
    (state) => state.deliveryCenter.deliveryCenter?.lng
  );

  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [searching, setSearching] = useState(false);
  const [success, setSuccess] = useState(false);

  // Address fields
  const [cep, setCep] = useState("");
  const [street, setStreet] = useState("");
  const [houseNumber, setHouseNumber] = useState("");
  const [neighborhood, setNeighborhood] = useState("");
  const [city, setCity] = useState("");
  const [uf, setUf] = useState("");
  const [complement, setComplement] = useState("");
  const [reference, setReference] = useState("");

  // Calculation fields
  const [addressLat, setAddressLat] = useState("");
  const [addressLng, setAddressLng] = useState("");
  const [distance, setDistance] = useState(0);
  const [deliveryFeeList, setDeliveryFeeList] = useState([]);
  const [deliveryFee, setDeliveryFee] = useState(null);
  const [calculated, setCalculated] = useState(false);

  // Form validation
  const [submitted, setSubmitted] = useState(false);
  const [formErrors, setFormErrors] = useState({
    cep: false,
    street: false,
    houseNumber: false,
    neighborhood: false,
    city: false,
    uf: false,
  });

  // Error handling
  const [openErrorAlert, setOpenErrorAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState(
    ERROR_MESSAGE_ADDRESS_NOT_FOUND
  );

  useEffect(() => {
    if (address) {
      setCep(address.cep || "");
      setStreet(address.street || "");
      setHouseNumber(address.house_number || "");
      setNeighborhood(address.neighborhood || "");
      setCity(address.city || "");
      setUf(address.uf || "");
      setComplement(address.address_complement || "");
      setReference(address.address_reference || "");
      setAddressLat(address.lat || "");
      setAddressLng(address.long || "");
      setDistance(address.distance || 0);
      setDeliveryFee(address.fee || null);
      setCalculated(!!address.fee);

      if (address.fee) {
        setActiveStep(2);
      }
    }
  }, [address]);

  const setDataFromCep = async () => {
    if (cep.length === 8) {
      try {
        setSearching(true);
        setOpenErrorAlert(false);

        const data = await searchCep(cep);

        if (!data || data.erro) {
          setErrorMessage("CEP não encontrado. Verifique o número informado.");
          setOpenErrorAlert(true);
          setSearching(false);
          return;
        }

        setStreet(data.logradouro || "");
        setNeighborhood(data.bairro || "");
        setCity(data.localidade || "");
        setUf(data.uf || "");

        // If we got valid data, move to next step
        if (data.logradouro && data.bairro && data.localidade && data.uf) {
          setActiveStep(1);
        }
      } catch (err) {
        setErrorMessage(
          "Erro ao buscar CEP. Tente novamente ou preencha manualmente."
        );
        setOpenErrorAlert(true);
      } finally {
        setSearching(false);
      }
    }
  };

  const validateForm = () => {
    setSubmitted(true);

    const errors = {
      cep: !validateCEP(cep),
      street: !validateString(street),
      houseNumber: !validateString(houseNumber),
      neighborhood: !validateString(neighborhood),
      city: !validateString(city),
      uf: !validateString(uf),
    };

    setFormErrors(errors);

    return !Object.values(errors).some((error) => error);
  };

  const calculateDistance = async () => {
    try {
      setLoading(true);
      setOpenErrorAlert(false);
      const isValid = validateForm();

      if (!isValid) {
        setErrorMessage("Preencha todos os campos obrigatórios corretamente.");
        setOpenErrorAlert(true);
        setLoading(false);
        return;
      }

      const address_coord = await addressCoordinates(
        street,
        houseNumber,
        neighborhood,
        city,
        cep
      );

      console.log({
        deliveryCenterLat,
        deliveryCenterLng,
      });

      const res = await AddressService.calculateDistance(
        deliveryCenterLat,
        deliveryCenterLng,
        address_coord.lat.toFixed(7),
        address_coord.lng.toFixed(7)
      );

      setAddressLat(address_coord.lat);
      setAddressLng(address_coord.lng);

      const distanceResponse = res.data.distance;
      setDistance(distanceResponse.toFixed(2));

      await calculateDeliveryFeeBasedOnDistance(distanceResponse);

      setCalculated(true);
      setActiveStep(2);
      setLoading(false);
    } catch (e) {
      setErrorMessage(
        "Não foi possível calcular a distância. Verifique o endereço informado."
      );
      setOpenErrorAlert(true);
      setLoading(false);
    }
  };

  const calculateDeliveryFeeBasedOnDistance = async (distance) => {
    try {
      const { data } =
        await DeliveryRadiusService.calculateDeliveryRadiusDeliveryTax(
          distance
        );

      if (data.length > 0) {
        const treatedDeliveryFeeList = data.map((deliveryFee) => {
          return {
            label: deliveryFee.merchant_name
              ? `${deliveryFee.merchant_name} - ${formatFinance(
                  deliveryFee.delivery_tax
                )}`
              : `Taxa de Entrega - ${formatFinance(deliveryFee.delivery_tax)}`,
            value: deliveryFee.delivery_tax,
          };
        });
        setDeliveryFeeList(treatedDeliveryFeeList);
      }

      setDeliveryFee(data[0]?.delivery_tax || 0);
    } catch (e) {
      console.error("Error calculating delivery fee:", e);
    }
  };

  const handleChangeDeliveryFee = (newValue) => {
    setDeliveryFee(newValue);
  };

  const submitAddress = () => {
    if (!validateForm()) {
      setErrorMessage("Preencha todos os campos obrigatórios corretamente.");
      setOpenErrorAlert(true);
      return;
    }

    if (!deliveryFee) {
      setErrorMessage("Informe um valor para a taxa de entrega.");
      setOpenErrorAlert(true);
      return;
    }

    const numericDeliveryFee = parseFloat(
      deliveryFee.toString().replace(",", ".")
    );

    const addressObj = {
      address_id: address?.address_id || uuidv4(),
      street: street,
      neighborhood: neighborhood,
      house_number: houseNumber,
      address_complement: complement,
      address_reference: reference,
      cep: cep,
      city: city,
      uf: uf,
      lat: parseFloat(addressLat),
      long: parseFloat(addressLng),
      fee: numericDeliveryFee,
      distance: distance,
    };

    setSuccess(true);
    setTimeout(() => {
      setAddress(addressObj);
    }, 1000);
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  return (
    <motion.div variants={containerVariants} initial="hidden" animate="visible">
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
      >
        {/* Error Alert */}
        <Collapse in={openErrorAlert}>
          <Alert
            severity="error"
            variant="filled"
            icon={<ErrorOutlineIcon />}
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => setOpenErrorAlert(false)}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
            sx={{ m: 2, borderRadius: 2 }}
          >
            {errorMessage}
          </Alert>
        </Collapse>

        {/* Success Message */}
        <Collapse in={success}>
          <Alert
            severity="success"
            variant="filled"
            icon={<CheckCircleIcon />}
            sx={{ m: 2, borderRadius: 2 }}
          >
            Endereço salvo com sucesso!
          </Alert>
        </Collapse>

        {/* Content */}
        <Box sx={{ p: 3 }}>
          <Stepper activeStep={activeStep} orientation="vertical">
            {/* Step 1: Enter CEP */}
            <Step>
              <StepLabel
                StepIconProps={{
                  icon: <MapIcon />,
                }}
              >
                <Typography variant="subtitle1" fontWeight="medium">
                  Informe o CEP
                </Typography>
              </StepLabel>
              <StepContent>
                <motion.div variants={itemVariants}>
                  <Box sx={{ mt: 2, mb: 3 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Digite o CEP para facilitar o preenchimento automático do
                      endereço
                    </Typography>

                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={2}
                      sx={{ mt: 2 }}
                    >
                      <TextField
                        id="cep"
                        label="CEP"
                        placeholder="Apenas números"
                        variant="outlined"
                        fullWidth
                        required
                        error={submitted && formErrors.cep}
                        value={cep}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, "");
                          setCep(value);
                        }}
                        onBlur={setDataFromCep}
                        InputProps={{
                          startAdornment: (
                            <MapIcon color="action" sx={{ mr: 1 }} />
                          ),
                        }}
                        inputProps={{ maxLength: 8 }}
                        helperText={
                          submitted && formErrors.cep
                            ? "Insira um CEP válido"
                            : "Formato: 00000000"
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                          },
                        }}
                      />

                      <motion.div
                        whileHover={!searching ? "hover" : undefined}
                        whileTap={!searching ? "tap" : undefined}
                        variants={buttonVariants}
                      >
                        <Button
                          variant="contained"
                          onClick={setDataFromCep}
                          disabled={cep.length !== 8 || searching}
                          startIcon={
                            searching ? (
                              <CircularProgress size={20} color="inherit" />
                            ) : (
                              <SearchIcon />
                            )
                          }
                          sx={{
                            borderRadius: 2,
                            height: 56,
                            px: 3,
                            minWidth: { xs: "100%", sm: 150 },
                          }}
                        >
                          {searching ? "Buscando..." : "Buscar CEP"}
                        </Button>
                      </motion.div>
                    </Stack>
                  </Box>

                  <Box
                    sx={{
                      mb: 2,
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Button
                      variant="text"
                      disabled
                      sx={{ visibility: "hidden" }}
                    >
                      Voltar
                    </Button>

                    <motion.div
                      whileHover="hover"
                      whileTap="tap"
                      variants={buttonVariants}
                    >
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleNext}
                        endIcon={<ArrowForwardIosIcon />}
                        sx={{ borderRadius: 2 }}
                      >
                        Preencher Manualmente
                      </Button>
                    </motion.div>
                  </Box>
                </motion.div>
              </StepContent>
            </Step>

            {/* Step 2: Address Details */}
            <Step>
              <StepLabel
                StepIconProps={{
                  icon: <HomeIcon />,
                }}
              >
                <Typography variant="subtitle1" fontWeight="medium">
                  Detalhes do Endereço
                </Typography>
              </StepLabel>
              <StepContent>
                <motion.div variants={itemVariants}>
                  <Grid container spacing={2} sx={{ mt: 1, mb: 3 }}>
                    <Grid item xs={12} sm={8}>
                      <TextField
                        id="street"
                        label="Rua"
                        placeholder="Ex: Avenida Paulista"
                        variant="outlined"
                        fullWidth
                        required
                        error={submitted && formErrors.street}
                        value={street}
                        onChange={(e) => setStreet(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <LocationOnIcon color="action" sx={{ mr: 1 }} />
                          ),
                        }}
                        helperText={
                          submitted && formErrors.street
                            ? "Campo obrigatório"
                            : ""
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        id="houseNumber"
                        label="Número"
                        placeholder="Ex: 1500"
                        variant="outlined"
                        fullWidth
                        required
                        error={submitted && formErrors.houseNumber}
                        value={houseNumber}
                        onChange={(e) => setHouseNumber(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <NumbersIcon color="action" sx={{ mr: 1 }} />
                          ),
                        }}
                        helperText={
                          submitted && formErrors.houseNumber
                            ? "Campo obrigatório"
                            : ""
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        id="neighborhood"
                        label="Bairro"
                        placeholder="Ex: Centro"
                        variant="outlined"
                        fullWidth
                        required
                        error={submitted && formErrors.neighborhood}
                        value={neighborhood}
                        onChange={(e) => setNeighborhood(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <ApartmentIcon color="action" sx={{ mr: 1 }} />
                          ),
                        }}
                        helperText={
                          submitted && formErrors.neighborhood
                            ? "Campo obrigatório"
                            : ""
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        id="city"
                        label="Cidade"
                        placeholder="Ex: São Paulo"
                        variant="outlined"
                        fullWidth
                        required
                        error={submitted && formErrors.city}
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <BusinessIcon color="action" sx={{ mr: 1 }} />
                          ),
                        }}
                        helperText={
                          submitted && formErrors.city
                            ? "Campo obrigatório"
                            : ""
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        id="uf"
                        label="Estado"
                        placeholder="Ex: SP"
                        variant="outlined"
                        fullWidth
                        required
                        error={submitted && formErrors.uf}
                        value={uf}
                        onChange={(e) => setUf(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <PublicIcon color="action" sx={{ mr: 1 }} />
                          ),
                        }}
                        helperText={
                          submitted && formErrors.uf ? "Campo obrigatório" : ""
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      <TextField
                        id="complement"
                        label="Complemento"
                        placeholder="Ex: Apto 42, Bloco B"
                        variant="outlined"
                        fullWidth
                        value={complement}
                        onChange={(e) => setComplement(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <HomeIcon color="action" sx={{ mr: 1 }} />
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        id="reference"
                        label="Referência"
                        placeholder="Ex: Próximo ao Shopping"
                        variant="outlined"
                        fullWidth
                        value={reference}
                        onChange={(e) => setReference(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <BookmarkIcon color="action" sx={{ mr: 1 }} />
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Grid>
                  </Grid>

                  <Box
                    sx={{
                      mb: 2,
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={handleBack}
                      sx={{ borderRadius: 2 }}
                    >
                      Voltar
                    </Button>

                    <motion.div
                      whileHover={!loading ? "hover" : undefined}
                      whileTap={!loading ? "tap" : undefined}
                      variants={buttonVariants}
                    >
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={calculateDistance}
                        disabled={loading}
                        startIcon={
                          loading ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            <CalculateIcon />
                          )
                        }
                        sx={{ borderRadius: 2 }}
                      >
                        {loading ? "Calculando..." : "Calcular Frete"}
                      </Button>
                    </motion.div>
                  </Box>
                </motion.div>
              </StepContent>
            </Step>

            {/* Step 3: Delivery Fee */}
            <Step>
              <StepLabel
                StepIconProps={{
                  icon: <LocalShippingIcon />,
                }}
              >
                <Typography variant="subtitle1" fontWeight="medium">
                  Taxa de Entrega
                </Typography>
              </StepLabel>
              <StepContent>
                <motion.div variants={itemVariants}>
                  <Paper
                    component={motion.div}
                    variants={resultCardVariants}
                    initial="hidden"
                    animate="visible"
                    elevation={0}
                    sx={{
                      p: 3,
                      mt: 2,
                      mb: 3,
                      borderRadius: 2,
                      border: `1px solid ${alpha(
                        theme.palette.success.main,
                        0.3
                      )}`,
                      bgcolor: alpha(theme.palette.success.main, 0.05),
                    }}
                  >
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 50,
                          height: 50,
                          bgcolor: alpha(theme.palette.success.main, 0.1),
                          color: theme.palette.success.main,
                          borderRadius: "50%",
                        }}
                      >
                        <DirectionsIcon fontSize="large" />
                      </Box>

                      <Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          Distância calculada
                        </Typography>
                        <Typography
                          variant="h4"
                          fontWeight="bold"
                          color="text.primary"
                        >
                          {distance} km
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>

                  {deliveryFeeList.length > 1 && (
                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant="subtitle2"
                        fontWeight="medium"
                        sx={{ mb: 1 }}
                      >
                        Selecione uma taxa de entrega
                      </Typography>
                      <Autocomplete
                        id="delivery-fee-list"
                        disablePortal
                        fullWidth
                        options={deliveryFeeList}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Taxa por Loja"
                            InputProps={{
                              ...params.InputProps,
                              startAdornment: (
                                <>
                                  <LocalShippingIcon
                                    color="action"
                                    sx={{ mr: 1 }}
                                  />
                                  {params.InputProps.startAdornment}
                                </>
                              ),
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: 2,
                              },
                            }}
                          />
                        )}
                        onChange={(event, values) => {
                          if (!values) return;
                          setDeliveryFee(values.value);
                        }}
                      />
                    </Box>
                  )}

                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle2"
                      fontWeight="medium"
                      sx={{ mb: 1 }}
                    >
                      Taxa de entrega
                    </Typography>
                    <MoneyTextField
                      value={deliveryFee}
                      setValue={handleChangeDeliveryFee}
                      label="Taxa de entrega"
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <AttachMoneyIcon color="action" sx={{ mr: 1 }} />
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                        },
                      }}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 1, display: "block" }}
                    >
                      Você pode ajustar o valor da taxa manualmente se
                      necessário
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      mb: 2,
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={handleBack}
                      sx={{ borderRadius: 2 }}
                    >
                      Voltar
                    </Button>

                    <motion.div
                      whileHover={!success ? "hover" : undefined}
                      whileTap={!success ? "tap" : undefined}
                      variants={buttonVariants}
                    >
                      <Button
                        variant="contained"
                        color="success"
                        onClick={submitAddress}
                        disabled={!deliveryFee || success}
                        startIcon={success ? <CheckCircleIcon /> : <SaveIcon />}
                        sx={{
                          borderRadius: 2,
                          px: 3,
                        }}
                      >
                        {success ? "Endereço Salvo!" : "Salvar Endereço"}
                      </Button>
                    </motion.div>
                  </Box>
                </motion.div>
              </StepContent>
            </Step>
          </Stepper>
        </Box>
      </Paper>
    </motion.div>
  );
}
